import React, { useMemo, useState } from "react";
import { useGetPatronsQuery } from "../api";
import { PatronListParams, PatronListItem } from "../types/patronage";
import PatronageEditModal from "../components/modals/PatronageEditModal";
import { UserAutocomplete } from "../components/UserAutocomplete";

// Patrons page component

const formatDateTime = (value: string | null): string => {
    if (!value) return "—";
    try {
        return new Date(value).toLocaleString("ru-RU", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return value;
    }
};

const PatronsPage: React.FC = () => {
    const [type, setType] = useState<1 | 2>(1);
    const [stage, setStage] = useState<1 | 2>(1);
    const [userIdInput, setUserIdInput] = useState<string>("");
    const [enabledFilter, setEnabledFilter] = useState<"" | "1" | "0">("");
    const [sortBy, setSortBy] =
        useState<PatronListParams["sort_by"]>("amount");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
    const [page, setPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(20);

    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    const params: PatronListParams = useMemo(() => {
        const parsedUserId = parseInt(userIdInput, 10);

        return {
            type,
            stage,
            user_id: !Number.isNaN(parsedUserId) && parsedUserId > 0 ? parsedUserId : undefined,
            enabled:
                enabledFilter === ""
                    ? undefined
                    : enabledFilter === "1"
                      ? true
                      : false,
            sort_by: sortBy || undefined,
            sort_order: sortOrder,
            page,
            per_page: perPage,
        };
    }, [
        enabledFilter,
        page,
        perPage,
        sortBy,
        sortOrder,
        stage,
        type,
        userIdInput,
    ]);

    const { data, isLoading, error } = useGetPatronsQuery(params);

    const openEdit = (userId: number) => {
        setSelectedUserId(userId);
        setIsEditModalOpen(true);
    };

    const closeEdit = () => {
        setIsEditModalOpen(false);
        setSelectedUserId(null);
    };

    const patrons = data?.data || [];
    const meta = data?.meta;

    return (
        <div className="h-screen flex flex-col">
            {/* Заголовок */}
            <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 p-6">
                <div className="flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Меценаты
                    </h2>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                        {meta?.total !== undefined ? (
                            <span>Всего: {meta.total}</span>
                        ) : null}
                    </div>
                </div>
            </div>

            {/* Фильтры */}
            <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Тип
                        </label>
                        <select
                            value={type}
                            onChange={(e) => {
                                setType(Number(e.target.value) as 1 | 2);
                                setPage(1);
                            }}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                            <option value={1}>Серебро</option>
                            <option value={2}>Золото</option>
                        </select>
                    </div>
                    

                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Этап
                        </label>
                        <select
                            value={stage}
                            onChange={(e) => {
                                setStage(Number(e.target.value) as 1 | 2);
                                setPage(1);
                            }}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                            <option value={1}>1</option>
                            <option value={2}>2</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Пользователь
                        </label>
                        <UserAutocomplete
                            value={userIdInput}
                            onChange={(v) => {
                                setUserIdInput(v);
                                setPage(1);
                            }}
                            placeholder="ID, email, phone, name..."
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Включено/Выключено
                        </label>
                        <select
                            value={enabledFilter}
                            onChange={(e) => {
                                setEnabledFilter(e.target.value as "" | "1" | "0");
                                setPage(1);
                            }}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                            <option value="">Все</option>
                            <option value="1">Включено</option>
                            <option value="0">Выключено</option>
                        </select>
                    </div>
                </div>

                <div className="flex flex-col md:flex-row gap-4 mt-4 items-start md:items-end">
                    <div className="flex gap-2">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Сортировать по
                            </label>
                            <select
                                value={sortBy || "amount"}
                                onChange={(e) => {
                                    setSortBy(e.target.value as any);
                                    setPage(1);
                                }}
                                className="border border-gray-300 dark:border-gray-600 rounded-md px-3 pr-8 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                                <option value="amount">amount</option>
                                <option value="time">time</option>
                                <option value="id">id</option>
                                <option value="name">name</option>
                                <option value="surname">surname</option>
                                <option value="email">email</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                В порядке
                            </label>
                            <select
                                value={sortOrder}
                                onChange={(e) => {
                                    setSortOrder(e.target.value as "asc" | "desc");
                                    setPage(1);
                                }}
                                className="border border-gray-300 dark:border-gray-600 rounded-md px-3 pr-8 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                                <option value="desc">desc</option>
                                <option value="asc">asc</option>
                            </select>
                        </div>
                    </div>

                </div>
            </div>

            {/* Таблица */}
            <div className="flex-1  bg-white dark:bg-gray-800">
                {isLoading ? (
                    <div className="flex justify-center items-center py-10">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                ) : error ? (
                    <div className="p-6 text-red-600 dark:text-red-400">
                        Ошибка загрузки списка меценатов
                    </div>
                ) : (
                    <table className="min-w-full table-fixed">
                        <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0 z-10">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[80px]">
                                    ID
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Пользователь
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[120px]">
                                    Amount
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[180px]">
                                    Time
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[120px]">
                                    Enabled
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[130px]">
                                    Coef
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-[140px]">
                                    Действия
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {patrons.map((p: PatronListItem) => (
                                (() => {
                                    const isEnabled = p.patronage_enabled;
                                    const coefficient = p.patronage_coefficient;
                                    return (
                                <tr
                                    key={p.id}
                                    className="hover:bg-gray-50 dark:hover:bg-gray-900"
                                >
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                        {p.id}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                                            {p.name} {p.surname}
                                        </div>
                                        <div className="text-sm text-gray-600 dark:text-gray-400">
                                            {p.email}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                        {new Intl.NumberFormat("ru-RU", {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        }).format(p.amount || 0)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                                        {formatDateTime(p.time)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <span
                                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                                isEnabled
                                                    ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                                    : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                                            }`}
                                        >
                                            {isEnabled ? "Да" : "Нет"}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                        {coefficient}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <button
                                            onClick={() => openEdit(p.id)}
                                            className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
                                        >
                                            Редактировать
                                        </button>
                                    </td>
                                </tr>
                                    );
                                })()
                            ))}

                            {patrons.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={7}
                                        className="px-6 py-8 text-center text-gray-600 dark:text-gray-400"
                                    >
                                        Нет данных
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Пагинация */}
            {meta && meta.last_page > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                            Показано{" "}
                            {meta.total > 0 ? (meta.current_page - 1) * meta.per_page + 1 : 0}{" "}
                            -{" "}
                            {Math.min(meta.current_page * meta.per_page, meta.total)}{" "}
                            из {meta.total}
                        </div>
                        <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                                <label className="text-sm text-gray-700 dark:text-gray-300">
                                    На странице:
                                </label>
                                <select
                                    value={meta.per_page}
                                    onChange={(e) => {
                                        setPerPage(Number(e.target.value));
                                        setPage(1);
                                    }}
                                    className="border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700"
                                >
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                            <div className="flex space-x-2">
                                <button
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={meta.current_page === 1}
                                    className="px-3 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 bg-white border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Назад
                                </button>
                                <span className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Стр. {meta.current_page} из {meta.last_page}
                                </span>
                                <button
                                    onClick={() =>
                                        setPage((p) => Math.min(meta.last_page, p + 1))
                                    }
                                    disabled={meta.current_page === meta.last_page}
                                    className="px-3 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 bg-white border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Вперед
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {selectedUserId !== null && (
                <PatronageEditModal
                    isOpen={isEditModalOpen}
                    userId={selectedUserId}
                    onClose={closeEdit}
                    onUpdated={() => {
                        // RTK Query автоматически обновит данные через invalidatesTags
                    }}
                />
            )}
        </div>
    );
};

export default PatronsPage;

